import type {ReactElement} from 'react';
import {useCallback, useEffect, useState} from 'react';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import {ControlledListItemType2} from '@Components/controlled-list/components/controlled-list-item-type-2';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import type {QRCodeFileExtension} from '@Libraries/qr-code/qr-code.types';
import {QR_GENERATION_SIZE_PX} from '@Libraries/qr-code/qr-render.library';
import {QR_ACTION_FEEDBACK_MS, QR_DEFAULT_DOWNLOAD_EXTENSION, QRCopyState} from '@Components/qr-code-generator/qr-code-generator.types';
import {getCopyFeedbackText, getCopyIconName} from '@Components/qr-code-generator/qr-code-generator.library';
import {useDismissOnOutsideInteraction, useIsCompactQRTool, useIsIconOnlyQRActions} from '@Components/qr-code-generator/qr-code-generator-hooks';
import {QRActionSheet} from '@Components/qr-code-generator/components/qr-action-sheet/qr-action-sheet';
import styles from './qr-code-actions.module.scss';
import type {QRCodeActionsProps, QRDownloadFormatRow} from './qr-code-actions.types';
import {QR_BLOCKED_HINT_COPY_KEY, QR_BLOCKED_HINT_DOWNLOAD_KEY, QR_DOWNLOAD_FORMAT_ROWS} from './qr-code-actions.types';

export function QRCodeActions({isCodeBlocked, payloadIssueText, customizeControl = null, onDownload, onCopy}: QRCodeActionsProps): ReactElement {
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState<QRCodeFileExtension>(QR_DEFAULT_DOWNLOAD_EXTENSION);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasDownloadFailed, setHasDownloadFailed] = useState(false);
  const [copyState, setCopyState] = useState<QRCopyState>(QRCopyState.IDLE);
  const isCompact = useIsCompactQRTool();
  const isIconOnly = useIsIconOnlyQRActions();

  const closeDownloadMenu = useCallback((): void => {
    setIsDownloadMenuOpen(false);
  }, []);
  const downloadMenuAnchorRef = useDismissOnOutsideInteraction(isDownloadMenuOpen && !isCompact, closeDownloadMenu);

  useEffect((): (() => void) | undefined => {
    if (copyState === QRCopyState.IDLE) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setCopyState(QRCopyState.IDLE);
    }, QR_ACTION_FEEDBACK_MS);
    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [copyState]);

  useEffect((): (() => void) | undefined => {
    if (!hasDownloadFailed) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setHasDownloadFailed(false);
    }, QR_ACTION_FEEDBACK_MS);
    return (): void => {
      window.clearTimeout(timeoutId);
    };
  }, [hasDownloadFailed]);

  const onFormatSelect = (rowId?: string): void => {
    const pickedRow = QR_DOWNLOAD_FORMAT_ROWS.find((row): boolean => {
      return row.extension === rowId;
    });
    if (pickedRow === undefined) {
      return;
    }
    setSelectedExtension(pickedRow.extension);
  };

  const onDownloadConfirm = async (): Promise<void> => {
    setIsDownloading(true);
    setHasDownloadFailed(false);
    try {
      await onDownload(selectedExtension);
      setIsDownloadMenuOpen(false);
    } catch (error) {
      window.PMW.log('The QR code could not be downloaded', error);
      setHasDownloadFailed(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const onCopyClick = async (): Promise<void> => {
    try {
      await onCopy();
      setCopyState(QRCopyState.COPIED);
    } catch (error) {
      window.PMW.log('The QR code could not be copied to the clipboard', error);
      setCopyState(QRCopyState.FAILED);
    }
  };

  const getFormatRowSubText = (row: QRDownloadFormatRow): string => {
    const description = window.i18next.t(row.descriptionKey);
    if (row.extension !== 'png') {
      return description;
    }
    return `${QR_GENERATION_SIZE_PX} × ${QR_GENERATION_SIZE_PX} px · ${description}`;
  };

  const renderFormatRows = (): ReactElement[] => {
    return QR_DOWNLOAD_FORMAT_ROWS.map((row): ReactElement => {
      return (
        <ControlledListItemType2
          key={row.extension}
          id={row.extension}
          className={styles.qrDownloadFormatRow}
          text={window.i18next.t(row.labelKey)}
          subText={getFormatRowSubText(row)}
          isSelected={selectedExtension === row.extension}
          thumbnail={{type: ThumbnailType.ICON_DEFAULT, icon: row.icon}}
          onClick={onFormatSelect}
        />
      );
    });
  };

  const renderActionFeedback = (feedbackText: string | null): ReactElement | null => {
    if (feedbackText === null) {
      return null;
    }
    return <Text val={feedbackText} size={TextSize.SM} className={styles.qrActionFeedback} attributes={{role: 'status'}} />;
  };

  const getDownloadFeedbackText = (): string | null => {
    return hasDownloadFailed ? window.i18next.t('pmwjs_qr_generator_download_failed') : null;
  };

  const renderDownloadChoices = (): ReactElement => {
    return (
      <>
        <ul className={styles.qrDownloadFormatList}>{renderFormatRows()}</ul>
        <Button
          text={window.i18next.t('pmwjs_qr_generator_download')}
          type={ButtonType.PRIMARY}
          size={ButtonSize.MEDIUM}
          icon="icon-cloud-download"
          isFullWidth
          isLoading={isDownloading}
          onClick={onDownloadConfirm}
        />
      </>
    );
  };

  const renderDownloadMenu = (): ReactElement | null => {
    if (!isDownloadMenuOpen) {
      return null;
    }
    if (isCompact) {
      return (
        <QRActionSheet label={window.i18next.t('pmwjs_qr_generator_download_format')} onClose={closeDownloadMenu}>
          {renderDownloadChoices()}
        </QRActionSheet>
      );
    }
    return (
      <div className={styles.qrDownloadMenu} role="group" aria-label={window.i18next.t('pmwjs_qr_generator_download_format')}>
        {renderDownloadChoices()}
      </div>
    );
  };

  /**
   * A payload the form can describe says its own problem for either action. With nothing to report, the hint names the action the visitor
   * actually reached for, so the copy button does not tell them to fill the form in order to download.
   */
  const getBlockedReason = (actionHintKey: string): string => {
    return payloadIssueText ?? window.i18next.t(actionHintKey);
  };

  const renderBlockedTooltip = (actionHintKey: string): ReactElement | null => {
    if (!isCodeBlocked) {
      return null;
    }
    return <Text val={getBlockedReason(actionHintKey)} size={TextSize.SM} className={styles.qrBlockedTooltip} attributes={{'aria-hidden': 'true'}} />;
  };

  const getActionAriaLabel = (actionLabel: string, actionHintKey: string): string => {
    return isCodeBlocked ? `${actionLabel} — ${getBlockedReason(actionHintKey)}` : actionLabel;
  };

  return (
    <div className={styles.qrCodeActions}>
      <div ref={downloadMenuAnchorRef} className={styles.qrDownloadControl}>
        <Button
          text={window.i18next.t('pmwjs_qr_generator_download')}
          type={ButtonType.PRIMARY}
          size={ButtonSize.MEDIUM}
          icon="icon-cloud-download"
          disabled={isCodeBlocked}
          isFullWidth
          isActive={isDownloadMenuOpen}
          customClasses={styles.qrActionButton}
          title={getActionAriaLabel(window.i18next.t('pmwjs_qr_generator_download'), QR_BLOCKED_HINT_DOWNLOAD_KEY)}
          attributes={{'aria-haspopup': 'true', 'aria-expanded': String(isDownloadMenuOpen)}}
          onClick={(): void => {
            setIsDownloadMenuOpen(!isDownloadMenuOpen);
          }}
        />
        {renderDownloadMenu()}
        {renderActionFeedback(getDownloadFeedbackText())}
        {renderBlockedTooltip(QR_BLOCKED_HINT_DOWNLOAD_KEY)}
      </div>

      {customizeControl}

      <div className={styles.qrCopyControl}>
        <Button
          text={isIconOnly ? undefined : window.i18next.t('pmwjs_qr_generator_copy')}
          type={ButtonType.SECONDARY}
          size={ButtonSize.MEDIUM}
          icon={getCopyIconName(copyState)}
          disabled={isCodeBlocked}
          attributes={{'aria-label': getActionAriaLabel(window.i18next.t('pmwjs_qr_generator_copy'), QR_BLOCKED_HINT_COPY_KEY)}}
          customClasses={styles.qrActionButton}
          title={getActionAriaLabel(window.i18next.t('pmwjs_qr_generator_copy'), QR_BLOCKED_HINT_COPY_KEY)}
          onClick={(): void => {
            void onCopyClick();
          }}
        />
        {renderActionFeedback(getCopyFeedbackText(copyState))}
        {renderBlockedTooltip(QR_BLOCKED_HINT_COPY_KEY)}
      </div>
    </div>
  );
}
